import type {
  ColDef,
  IDoesFilterPassParams,
  IFilterComp,
  IFilterParams,
} from 'ag-grid-community';

type TextFilterModel = {
  filter: string;
};

type TextFilterParams = IFilterParams & {
  colDef: ColDef;
  // If true, skip client-side filtering (server handles filtering)
  serverSideFiltering?: boolean;
};

export class AgTextFilter implements IFilterComp<TextFilterModel> {
  private params!: TextFilterParams;

  private gui!: HTMLElement;

  private searchInput!: HTMLInputElement;

  private applyButton!: HTMLButtonElement;

  private resetButton!: HTMLButtonElement;

  private filterValue: string = '';

  private appliedFilterValue: string = '';

  public init(params: TextFilterParams): void {
    this.params = params;
    this.gui = document.createElement('div');
    this.gui.className =
      'ag-text-filter tw-flex tw-flex-col tw-gap-2 tw-p-2 tw-text-sm';
    this.gui.style.maxWidth = '250px';

    this.createSearchInput();
    this.createButtons();

    // Initialize with existing filter model if present
    const filterModel = params.api.getFilterModel();
    const colId = params.column.getColId();
    const existingFilter = filterModel[colId];
    if (existingFilter && existingFilter.filter) {
      this.filterValue = String(existingFilter.filter);
      this.appliedFilterValue = this.filterValue;
      this.searchInput.value = this.filterValue;
    }
  }

  public getGui(): HTMLElement {
    return this.gui;
  }

  public doesFilterPass(params: IDoesFilterPassParams): boolean {
    // If server-side filtering is enabled, always return true (server handles filtering)
    if (this.params.serverSideFiltering) {
      return true;
    }

    // If no filter value is applied, show all rows (filter is inactive)
    if (!this.isFilterActive()) {
      return true;
    }

    // Skip filtering for detail rows - they should always show if their parent passes
    if (params.node.data && (params.node.data as any).kind === 'detail') {
      return true;
    }

    const value = this.getValue(params);

    // Handle null/undefined values
    if (value === null || value === undefined) {
      return false;
    }

    // Convert to string and perform case-insensitive contains check
    const cellValue = String(value).toLowerCase();
    const filterText = this.appliedFilterValue.toLowerCase().trim();

    return cellValue.includes(filterText);
  }

  public isFilterActive(): boolean {
    // Filter is active only when there's a non-empty filter value
    return this.appliedFilterValue.trim().length > 0;
  }

  public getModel(): TextFilterModel | null {
    if (!this.isFilterActive()) {
      return null;
    }

    return {
      filter: this.appliedFilterValue,
    };
  }

  public setModel(model: TextFilterModel | null): void {
    if (!model || !model.filter) {
      // Clear filter when model is null or filter is empty
      this.filterValue = '';
      this.appliedFilterValue = '';
      this.searchInput.value = '';
      this.updateButtons();
      return;
    }

    this.filterValue = String(model.filter);
    this.appliedFilterValue = this.filterValue;
    this.searchInput.value = this.filterValue;
    this.updateButtons();
  }

  public afterGuiAttached(): void {
    this.searchInput?.focus();
  }

  public onNewRowsLoaded(): void {
    // No-op for text filter
  }

  public destroy(): void {
    // no-op
  }

  public refresh(): boolean {
    return true;
  }

  private createSearchInput() {
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.placeholder = 'Search...';
    this.searchInput.className =
      'tw-rounded tw-bg-[#1d2023] tw-border tw-border-[#30353b] tw-px-2 tw-py-1 tw-text-white tw-placeholder-neutral-500 focus:tw-outline-none focus:tw-border-[#4a5056]';
    this.searchInput.addEventListener('input', () => {
      this.filterValue = this.searchInput.value;
      this.updateButtons();
    });
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleApply();
      }
    });
    this.gui.appendChild(this.searchInput);
  }

  private createButtons() {
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'tw-flex tw-justify-end tw-gap-2';

    this.applyButton = document.createElement('button');
    this.applyButton.type = 'button';
    this.applyButton.textContent = 'Apply';
    this.applyButton.className =
      'tw-text-xs tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-cursor-pointer tw-border-none tw-rounded tw-px-3 tw-py-1 tw-transition-colors';
    this.applyButton.addEventListener('click', () => {
      this.handleApply();
    });

    this.resetButton = document.createElement('button');
    this.resetButton.type = 'button';
    this.resetButton.textContent = 'Reset';
    this.resetButton.className =
      'tw-text-xs tw-text-blue-400 hover:tw-text-blue-300 tw-cursor-pointer tw-bg-transparent tw-border-none tw-p-0 tw-px-1';
    this.resetButton.addEventListener('click', () => {
      this.handleReset();
    });

    buttonContainer.appendChild(this.applyButton);
    buttonContainer.appendChild(this.resetButton);
    this.gui.appendChild(buttonContainer);

    this.updateButtons();
  }

  private handleApply() {
    this.appliedFilterValue = this.filterValue;
    this.params.filterChangedCallback();
    this.updateButtons();
    // Close the popup menu after applying the filter
    this.params.api.hidePopupMenu();
  }

  private handleReset() {
    this.filterValue = '';
    this.appliedFilterValue = '';
    this.searchInput.value = '';
    this.params.filterChangedCallback();
    this.updateButtons();
    // Close the popup menu after resetting the filter
    this.params.api.hidePopupMenu();
  }

  private updateButtons() {
    // Enable/disable apply button based on whether value has changed
    const hasChanged = this.filterValue !== this.appliedFilterValue;
    this.applyButton.disabled = !hasChanged;
    this.applyButton.style.opacity = hasChanged ? '1' : '0.5';
    this.applyButton.style.cursor = hasChanged ? 'pointer' : 'not-allowed';

    // Show/hide reset button based on whether filter is active
    this.resetButton.style.display =
      this.appliedFilterValue.trim().length > 0 ? 'block' : 'none';
  }

  private getValue(params: IDoesFilterPassParams): any {
    const { colDef } = this.params;
    const valueGetter = colDef.filterValueGetter || colDef.valueGetter;

    if (valueGetter && typeof valueGetter === 'function') {
      return valueGetter({
        api: this.params.api,
        column: this.params.column,
        context: this.params.context,
        data: params.node.data,
        node: params.node,
      } as any);
    }

    if (colDef.field && params.node.data) {
      return (params.node.data as any)[colDef.field];
    }

    return null;
  }
}

